import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { FileJson, Store, CheckCircle2, AlertCircle, Loader2, Layers, UtensilsCrossed, Settings2, ChefHat, ArrowRight, ArrowLeft } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────
interface ParsedPayload {
  categories?: any[];
  items?: any[];
  options?: any[];
  stations?: any[];
}
interface UpdateResult {
  success: boolean;
  result?: {
    success: boolean;
    restaurant_id: number;
    categories_inserted: number;
    items_inserted: number;
    options_inserted: number;
    stations_created: number;
  };
}
interface ValidationState {
  status: 'idle' | 'valid' | 'error';
  errors: string[];
  counts: {
    categories: number;
    items: number;
    options: number;
    stations: number;
  };
}
interface RestaurantOption {
  id: number;
  name: string;
  slug: string;
}
// ─── Summary Card ────────────────────────────────────────────
function SummaryMetric({
  icon: Icon,
  label,
  value
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
}) {
  return <div className="bg-[#0B0B0B] border border-white/5 rounded-xl p-4 text-center" data-id="element-2757">
      <Icon className="w-4 h-4 text-white/30 mx-auto mb-2" data-id="element-2758" />
      <div className="text-2xl font-bold text-white" data-id="element-2759">{value}</div>
      <div className="text-[10px] text-white/40 uppercase tracking-widest mt-1" data-id="element-2760">
        {label}
      </div>
    </div>;
}
// ─── Page Component ──────────────────────────────────────────
export function EnrollmentMenuUpdatePage() {
  const navigate = useNavigate();
  // State
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  const [isLoadingRestaurants, setIsLoadingRestaurants] = useState(true);
  const [jsonInput, setJsonInput] = useState('');
  const [parsedData, setParsedData] = useState<ParsedPayload | null>(null);
  const [updateMode, setUpdateMode] = useState<'append' | 'replace'>('append');
  const [validation, setValidation] = useState<ValidationState>({
    status: 'idle',
    errors: [],
    counts: {
      categories: 0,
      items: 0,
      options: 0,
      stations: 0
    }
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<UpdateResult | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  // ─── Helper: generate slug ────────────────────────────────
  const generateSlug = (name: string): string => {
    return (name || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'item';
  };
  // ─── Fetch Restaurants ─────────────────────────────────────
  useEffect(() => {
    async function fetchRestaurants() {
      try {
        const {
          data,
          error
        } = await supabase.from('restaurants').select('id, name, slug').eq('is_active', true).order('name');
        if (error) throw error;
        setRestaurants(data || []);
      } catch (err) {
        console.error('Error fetching restaurants:', err);
      } finally {
        setIsLoadingRestaurants(false);
      }
    }
    fetchRestaurants();
  }, []);
  // ─── Validate ────────────────────────────────────────────
  const handleValidate = () => {
    setParsedData(null);
    setUpdateResult(null);
    setUpdateError(null);
    const errors: string[] = [];
    if (!selectedRestaurantId) {
      errors.push('Debes seleccionar un restaurante.');
    }
    if (!jsonInput.trim()) {
      setValidation({
        status: 'error',
        errors: ['El JSON está vacío.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    let data: any;
    try {
      data = JSON.parse(jsonInput);
    } catch {
      setValidation({
        status: 'error',
        errors: ['El JSON no es válido. Revisa la sintaxis.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    // Structural validation — must have at least one of categories, items, options
    let hasData = false;
    if (data.categories !== undefined) {
      if (!Array.isArray(data.categories)) errors.push('"categories" debe ser un array.');else if (data.categories.length > 0) hasData = true;
    }
    if (data.items !== undefined) {
      if (!Array.isArray(data.items)) errors.push('"items" debe ser un array.');else if (data.items.length > 0) hasData = true;
    }
    if (data.options !== undefined) {
      if (!Array.isArray(data.options)) errors.push('"options" debe ser un array.');else if (data.options.length > 0) hasData = true;
    }
    if (!hasData && errors.length === 0) {
      errors.push('El JSON debe incluir categories, items u options');
    }
    if (errors.length > 0) {
      setValidation({
        status: 'error',
        errors,
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    const counts = {
      categories: Array.isArray(data.categories) ? data.categories.length : 0,
      items: Array.isArray(data.items) ? data.items.length : 0,
      options: Array.isArray(data.options) ? data.options.length : 0,
      stations: 0
    };
    setParsedData(data as ParsedPayload);
    setValidation({
      status: 'valid',
      errors: [],
      counts
    });
  };
  // ─── Update ──────────────────────────────────────────────
  const handleUpdateMenu = async () => {
    if (!parsedData || !selectedRestaurantId) return;
    setIsUpdating(true);
    setUpdateError(null);
    setUpdateResult(null);
    const restaurantId = parseInt(selectedRestaurantId, 10);
    let categoriesInserted = 0;
    let itemsInserted = 0;
    let optionsInserted = 0;
    let stationsCreated = 0;
    try {
      // ─── REPLACE MODE: Delete existing data first ──────────
      if (updateMode === 'replace') {
        console.log('[MenuUpdate] Replace mode — deleting existing data...');
        // Delete in order: options → items → categories (foreign key deps)
        await supabase.from('menu_item_options').delete().eq('restaurant_id', restaurantId);
        await supabase.from('menu_items').delete().eq('restaurant_id', restaurantId);
        await supabase.from('menu_categories').delete().eq('restaurant_id', restaurantId);
        console.log('[MenuUpdate] Existing data deleted.');
      }
      // ─── INSERT CATEGORIES ─────────────────────────────────
      const categories = parsedData.categories || [];
      const categorySlugToId: Record<string, string> = {};
      if (categories.length > 0) {
        // Get existing max sort_order
        const {
          data: existingCats
        } = await supabase.from('menu_categories').select('sort_order').eq('restaurant_id', restaurantId).order('sort_order', {
          ascending: false
        }).limit(1);
        let nextSort = (existingCats?.[0]?.sort_order ?? 0) + 1;
        for (const cat of categories) {
          const slug = generateSlug(cat.name);
          const payload = {
            restaurant_id: restaurantId,
            name: cat.name,
            slug,
            description: cat.description || null,
            is_active: cat.is_active ?? true,
            sort_order: cat.sort_order ?? nextSort++
          };
          console.log('[MenuUpdate] Inserting category:', payload.name);
          const {
            data,
            error
          } = await supabase.from('menu_categories').insert(payload).select('id, slug').single();
          if (error) {
            console.error('[MenuUpdate] Category insert error:', error);
            // Try to continue — might be a duplicate
            // Look up existing category by name
            const {
              data: existing
            } = await supabase.from('menu_categories').select('id, slug').eq('restaurant_id', restaurantId).eq('name', cat.name).single();
            if (existing) {
              categorySlugToId[cat.name] = existing.id;
              categorySlugToId[slug] = existing.id;
              if (cat.slug) categorySlugToId[cat.slug] = existing.id;
            }
          } else if (data) {
            categoriesInserted++;
            categorySlugToId[cat.name] = data.id;
            categorySlugToId[slug] = data.id;
            if (cat.slug) categorySlugToId[cat.slug] = data.id;
          }
        }
      }
      // Also fetch all existing categories for item matching
      const {
        data: allCats
      } = await supabase.from('menu_categories').select('id, name, slug').eq('restaurant_id', restaurantId);
      if (allCats) {
        for (const c of allCats) {
          categorySlugToId[c.name] = c.id;
          categorySlugToId[c.slug] = c.id;
        }
      }
      // ─── INSERT ITEMS ──────────────────────────────────────
      const items = parsedData.items || [];
      const itemNameToId: Record<string, string> = {};
      if (items.length > 0) {
        const {
          data: existingItems
        } = await supabase.from('menu_items').select('sort_order').eq('restaurant_id', restaurantId).order('sort_order', {
          ascending: false
        }).limit(1);
        let nextItemSort = (existingItems?.[0]?.sort_order ?? 0) + 1;
        for (const item of items) {
          // Resolve category_id from name, slug, or direct id
          let categoryId = item.category_id || null;
          if (!categoryId && item.category) {
            categoryId = categorySlugToId[item.category] || null;
          }
          if (!categoryId && item.category_name) {
            categoryId = categorySlugToId[item.category_name] || null;
          }
          if (!categoryId && item.category_slug) {
            categoryId = categorySlugToId[item.category_slug] || null;
          }
          const slug = generateSlug(item.name);
          const payload = {
            restaurant_id: restaurantId,
            category_id: categoryId,
            name: item.name,
            slug,
            description: item.description || null,
            price: parseFloat(item.price) || 0,
            image_url: item.image_url || null,
            station_code: item.station_code || null,
            is_active: item.is_active ?? true,
            sort_order: item.sort_order ?? nextItemSort++,
            is_customizable: item.is_customizable ?? false,
            item_type: item.item_type || null
          };
          console.log('[MenuUpdate] Inserting item:', payload.name);
          const {
            data,
            error
          } = await supabase.from('menu_items').insert(payload).select('id').single();
          if (error) {
            console.error('[MenuUpdate] Item insert error:', error, 'for:', item.name);
          } else if (data) {
            itemsInserted++;
            itemNameToId[item.name] = data.id;
            if (item.slug) itemNameToId[item.slug] = data.id;
          }
        }
      }
      // Also fetch all existing items for option matching
      const {
        data: allItems
      } = await supabase.from('menu_items').select('id, name, slug').eq('restaurant_id', restaurantId);
      if (allItems) {
        for (const i of allItems) {
          itemNameToId[i.name] = i.id;
          if (i.slug) itemNameToId[i.slug] = i.id;
        }
      }
      // ─── INSERT OPTIONS ────────────────────────────────────
      const options = parsedData.options || [];
      if (options.length > 0) {
        for (const opt of options) {
          let menuItemId = opt.menu_item_id || null;
          if (!menuItemId && opt.item_name) {
            menuItemId = itemNameToId[opt.item_name] || null;
          }
          if (!menuItemId && opt.item_slug) {
            menuItemId = itemNameToId[opt.item_slug] || null;
          }
          if (!menuItemId) {
            console.warn('[MenuUpdate] Could not resolve item for option:', opt.name);
            continue;
          }
          const payload = {
            restaurant_id: restaurantId,
            menu_item_id: menuItemId,
            option_group: opt.option_group || opt.group || 'Opciones',
            name: opt.name,
            price_delta: parseFloat(opt.price_delta) || 0,
            is_required: opt.is_required ?? false,
            max_select: opt.max_select ?? 1,
            is_active: opt.is_active ?? true,
            sort_order: opt.sort_order ?? 0
          };
          const {
            error
          } = await supabase.from('menu_item_options').insert(payload);
          if (error) {
            console.error('[MenuUpdate] Option insert error:', error, 'for:', opt.name);
          } else {
            optionsInserted++;
          }
        }
      }
      // ─── INSERT STATIONS ───────────────────────────────────
      const stations = parsedData.stations || [];
      if (stations.length > 0) {
        for (const station of stations) {
          const {
            error
          } = await supabase.from('kitchen_stations').insert({
            restaurant_id: restaurantId,
            name: station.name,
            code: station.code || generateSlug(station.name),
            sort_order: station.sort_order ?? 0,
            is_active: true
          });
          if (!error) stationsCreated++;else console.error('[MenuUpdate] Station insert error:', error);
        }
      }
      console.log('[MenuUpdate] Done!', {
        categoriesInserted,
        itemsInserted,
        optionsInserted,
        stationsCreated
      });
      setUpdateResult({
        success: true,
        result: {
          success: true,
          restaurant_id: restaurantId,
          categories_inserted: categoriesInserted,
          items_inserted: itemsInserted,
          options_inserted: optionsInserted,
          stations_created: stationsCreated
        }
      });
    } catch (error: any) {
      console.error('[MenuUpdate] Error:', error);
      setUpdateError(error instanceof Error ? error.message : 'Ocurrió un error inesperado al actualizar el menú.');
    } finally {
      setIsUpdating(false);
    }
  };
  // ─── Render Success ──────────────────────────────────────────────
  if (updateResult?.success && updateResult.result) {
    const res = updateResult.result;
    const selectedRestaurant = restaurants.find(r => r.id.toString() === selectedRestaurantId);
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-2761">
        <div className="max-w-2xl w-full space-y-8 text-center" data-id="element-2762">
          <div className="w-24 h-24 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto" data-id="element-2763">
            <CheckCircle2 className="w-12 h-12 text-amber-500" data-id="element-2764" />
          </div>
          <div data-id="element-2765">
            <h1 className="text-3xl font-black tracking-tight" data-id="element-2766">
              ¡Menú Actualizado!
            </h1>
            <p className="text-white/50 mt-2" data-id="element-2767">
              Los nuevos elementos han sido agregados exitosamente al
              restaurante.
            </p>
          </div>

          <div className="bg-[#141414] border border-amber-500/30 rounded-2xl p-6 text-left space-y-6" data-id="element-2768">
            <div className="flex items-center justify-between pb-4 border-b border-white/5" data-id="element-2769">
              <div data-id="element-2770">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2771">
                  Restaurante
                </p>
                <p className="text-xl font-bold text-white mt-1" data-id="element-2772">
                  {selectedRestaurant?.name || 'Desconocido'}
                </p>
              </div>
              <div className="text-right" data-id="element-2773">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2774">
                  ID
                </p>
                <p className="text-lg font-mono text-amber-500 mt-1" data-id="element-2775">
                  #{res.restaurant_id}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-2776">
              <SummaryMetric icon={Layers} label="Categorías" value={res.categories_inserted || 0} data-id="element-2777" />
              <SummaryMetric icon={UtensilsCrossed} label="Productos" value={res.items_inserted || 0} data-id="element-2778" />
              <SummaryMetric icon={Settings2} label="Opciones" value={res.options_inserted || 0} data-id="element-2779" />
              <SummaryMetric icon={ChefHat} label="Estaciones" value={res.stations_created || 0} data-id="element-2780" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4" data-id="element-2781">
            <button onClick={() => {
            setUpdateResult(null);
            setJsonInput('');
            setParsedData(null);
            setValidation({
              status: 'idle',
              errors: [],
              counts: {
                categories: 0,
                items: 0,
                options: 0,
                stations: 0
              }
            });
          }} className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-bold transition-colors" data-id="element-2782">
              Actualizar otro
            </button>
            <button onClick={() => navigate(`/admin/${selectedRestaurant?.slug || ''}`)} className="px-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2" data-id="element-2783">
              Ver restaurante <ArrowRight className="w-5 h-5" data-id="element-2784" />
            </button>
          </div>
        </div>
      </div>;
  }
  // ─── Render Main ──────────────────────────────────────────────
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans selection:bg-amber-500/30 selection:text-amber-200" data-id="element-2785">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-8" data-id="element-2786">
        {/* Header */}
        <header className="flex items-start gap-4" data-id="element-2787">
          <button onClick={() => navigate('/admin/enrollment')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors shrink-0 mt-2" data-id="element-2788">
            <ArrowLeft className="w-5 h-5" data-id="element-2789" />
          </button>
          <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center shrink-0" data-id="element-2790">
            <UtensilsCrossed className="w-7 h-7 text-amber-500" data-id="element-2791" />
          </div>
          <div data-id="element-2792">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-id="element-2793">
              Actualizar menú existente
            </h1>
            <p className="text-white/40 text-sm mt-1 max-w-xl" data-id="element-2794">
              Agrega categorías, productos u opciones a un restaurante ya creado
              usando el JSON generado por el parser.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2795">
          {/* ─── Left: JSON Input ─── */}
          <div className="lg:col-span-7 space-y-6" data-id="element-2796">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/20" data-id="element-2797">
              <div className="flex items-center justify-between mb-4" data-id="element-2798">
                <h2 className="text-base font-semibold flex items-center gap-2" data-id="element-2799">
                  <FileJson className="w-5 h-5 text-white/40" data-id="element-2800" />
                  Payload JSON
                </h2>
                <button onClick={handleValidate} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium transition-all active:scale-95" data-id="element-2801">
                  Validar JSON
                </button>
              </div>

              <textarea value={jsonInput} onChange={e => setJsonInput(e.target.value)} placeholder="Pega aquí el JSON con las nuevas categorías, items, opciones o estaciones..." className="w-full h-[420px] bg-[#0B0B0B] border border-white/10 rounded-xl p-4 font-mono text-[13px] leading-relaxed text-white/80 placeholder:text-white/15 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 resize-y transition-colors" spellCheck={false} data-id="element-2802" />

              {/* Validation Error */}
              {validation.status === 'error' && <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-2803">
                  <div className="flex items-center gap-2 text-red-400 mb-2" data-id="element-2804">
                    <AlertCircle className="w-4 h-4" data-id="element-2805" />
                    <strong className="text-sm font-semibold" data-id="element-2806">
                      Errores de validación
                    </strong>
                  </div>
                  <ul className="list-disc list-inside text-sm text-red-300/80 space-y-1" data-id="element-2807">
                    {validation.errors.map((err, i) => <li key={i} data-id="element-2808">{err}</li>)}
                  </ul>
                </div>}
            </div>
          </div>

          {/* ─── Right: Preview & Actions ─── */}
          <div className="lg:col-span-5 space-y-6" data-id="element-2809">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 sticky top-8" data-id="element-2810">
              <h2 className="text-base font-semibold mb-6 flex items-center gap-2" data-id="element-2811">
                <Store className="w-5 h-5 text-white/40" data-id="element-2812" />
                Destino de la actualización
              </h2>

              <div className="space-y-5" data-id="element-2813">
                {/* Restaurant Selector */}
                <div data-id="element-2814">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2815">
                    Restaurante
                  </label>
                  <div className="relative" data-id="element-2816">
                    <select value={selectedRestaurantId} onChange={e => {
                    setSelectedRestaurantId(e.target.value);
                    if (validation.status === 'valid') {
                      setValidation({
                        status: 'idle',
                        errors: [],
                        counts: {
                          categories: 0,
                          items: 0,
                          options: 0,
                          stations: 0
                        }
                      });
                    }
                  }} disabled={isLoadingRestaurants} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-amber-500 disabled:opacity-50 appearance-none" data-id="element-2817">
                      <option value="" disabled data-id="element-2818">
                        Selecciona un restaurante...
                      </option>
                      {restaurants.map(r => <option key={r.id} value={r.id} data-id="element-2819">
                          {r.name} — /{r.slug}
                        </option>)}
                    </select>
                    {isLoadingRestaurants && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 animate-spin" data-id="element-2820" />}
                  </div>
                </div>

                {/* Mode Selector */}
                <div data-id="element-2821">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2822">
                    Modo de actualización
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-id="element-2823">
                    <button type="button" onClick={() => setUpdateMode('append')} className={`p-3 rounded-xl border text-left transition-all ${updateMode === 'append' ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400' : 'bg-[#0B0B0B] border-white/10 text-white/50 hover:border-white/20'}`} data-id="element-2824">
                      <span className="text-sm font-bold block" data-id="element-2825">
                        ➕ Solo agregar
                      </span>
                      <span className="text-[10px] opacity-70 block mt-0.5" data-id="element-2826">
                        Mantiene los productos existentes
                      </span>
                    </button>
                    <button type="button" onClick={() => setUpdateMode('replace')} className={`p-3 rounded-xl border text-left transition-all ${updateMode === 'replace' ? 'bg-red-500/10 border-red-500/40 text-red-400' : 'bg-[#0B0B0B] border-white/10 text-white/50 hover:border-white/20'}`} data-id="element-2827">
                      <span className="text-sm font-bold block" data-id="element-2828">
                        🔄 Reemplazar
                      </span>
                      <span className="text-[10px] opacity-70 block mt-0.5" data-id="element-2829">
                        Elimina el menú actual y lo reemplaza
                      </span>
                    </button>
                  </div>
                </div>

                {/* Counts Summary */}
                {validation.status === 'valid' && <div className="pt-6 border-t border-white/10" data-id="element-2830">
                    <div className="flex items-center gap-2 text-amber-500 mb-4" data-id="element-2831">
                      <CheckCircle2 className="w-4 h-4" data-id="element-2832" />
                      <span className="text-sm font-bold" data-id="element-2833">JSON Válido</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-2834">
                      <SummaryMetric icon={Layers} label="Categorías" value={validation.counts.categories} data-id="element-2835" />
                      <SummaryMetric icon={UtensilsCrossed} label="Productos" value={validation.counts.items} data-id="element-2836" />
                      <SummaryMetric icon={Settings2} label="Opciones" value={validation.counts.options} data-id="element-2837" />
                      <SummaryMetric icon={ChefHat} label="Estaciones" value={validation.counts.stations} data-id="element-2838" />
                    </div>
                  </div>}

                {/* Create Error */}
                {updateError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-2839">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-2840" />
                    <div className="text-sm whitespace-pre-line" data-id="element-2841">
                      {updateError}
                    </div>
                  </div>}

                {/* Action Button */}
                <button onClick={handleUpdateMenu} disabled={validation.status !== 'valid' || isUpdating || !selectedRestaurantId} className="w-full py-4 bg-amber-600 hover:bg-amber-500 disabled:bg-white/5 disabled:text-white/30 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-2842">
                  {isUpdating ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-2843" />
                      Actualizando menú...
                    </> : <>
                      <UtensilsCrossed className="w-5 h-5" data-id="element-2844" />
                      Actualizar menú
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}